import React, { useMemo } from "react";
import { useLocation, useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, Home as HomeIcon, ArrowLeft } from "lucide-react";
import { useBreadcrumbLabels } from "@/lib/breadcrumb-labels";

/**
 * Sticky breadcrumbs — appears at the top of the main content on routes
 * 3+ levels deep so users always know where they are and can jump back
 * one level without hunting for a "back" button.
 *
 * Hidden on:
 *   • Top-level routes (single segment) — sidebar already conveys location.
 *   • /login, /accept-invite — pre-auth.
 *
 * Labels are derived from the URL with three-tier resolution (per segment):
 *   1. `useBreadcrumbLabels()` — dynamic registry: entity ID → friendly
 *      name that the entity's page pushes into a shared React context
 *      (see `lib/breadcrumb-labels.jsx`). Used for `/dashboards/{id}`,
 *      `/app-catalog/my-apps/{iid}`, `/gallery/preview/{id}`, etc. so
 *      the breadcrumb reads "Price Optimization App" instead of a UUID.
 *   2. `PATH_LABELS` — static overrides for known top-level sections.
 *   3. Auto-derivation from the raw segment (Title Case, or truncated
 *      hash for opaque IDs that no page has registered a name for).
 *
 * Last segment is bold + non-clickable.
 */
const PATH_LABELS = {
    "dashboards": "Dashboards",
    "data": "Data",
    "data-models": "Data Models",
    "data_models": "Data Models",
    "sense-ai": "Sense AI",
    "settings": "Settings",
    "users": "Users",
    "organizations": "Organizations",
    "operations": "Operations",
    "reports": "Reports",
    "audit-activity": "Activity Log",
    "audit_activity": "Activity Log",
    "subscription": "Subscription",
    "feature-catalog": "Feature Catalog",
    "scope-matrix": "Who sees what",
    "feature_catalog": "Feature Catalog",
    "business-rules": "Business Rules",
    "business_rules": "Business Rules",
    "release-notes": "Release Notes",
    "release_notes": "Release Notes",
    "gallery": "Gallery",
    "preview": "Preview",
    "workspace": "Workspace",
    "app-catalog": "App Catalog",
    "app_catalog": "App Catalog",
    "my-apps": "My Apps",
    "app-studio": "App Studio",
    "app_studio": "App Studio",
    "playbooks": "Playbooks",
    "platform": "Platform",
    "connectors": "Connectors",
    "templates": "Templates",
    "yaml-connectors": "YAML Connectors",
    "documentation": "Documentation",
    "billing": "Billing",
    "activity": "Activity",
    "audit-log": "Audit Log",
    "setup-checklist": "Setup Checklist",
    "branding": "Branding",
    "tracking": "Header & Footer Scripts",
};

const HIDDEN_PREFIXES = ["/login", "/accept-invite", "/accept-workspace-invite",
                         "/forgot-password", "/reset-password",
                         // Pages with their own self-contained header shell
                         // would just create a height mismatch with main if we
                         // also rendered the breadcrumb strip — Sense AI's
                         // h-[calc(100vh-60px)] root assumes the breadcrumb
                         // band is absent.
                         "/sense-ai"];


function _label(seg, dynamicLabels) {
    // 1) Page-registered friendly name for this exact segment wins.
    if (dynamicLabels && dynamicLabels[seg]) return dynamicLabels[seg];
    // 2) Static, path-level overrides for known sections.
    if (PATH_LABELS[seg]) return PATH_LABELS[seg];
    // 3) Opaque IDs (UUIDs / hashes) — render as a truncated hint so
    //    the breadcrumb doesn't blow out horizontally. Pages that own
    //    the entity should register a proper name via
    //    useBreadcrumbLabel(id, name) to override this fallback.
    if (/^[0-9a-f]{8,}/i.test(seg)) return seg.length > 12 ? seg.slice(0, 8) + "…" : seg;
    return seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}


export default function Breadcrumbs() {
    const location = useLocation();
    const navigate = useNavigate();
    const path = location.pathname || "";
    const segments = useMemo(() => path.split("/").filter(Boolean), [path]);
    const dynamicLabels = useBreadcrumbLabels();

    if (HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return null;
    // Hide ONLY on the bare home `/` route. Show breadcrumbs on every other
    // authenticated route — keeps the location visible after deep navigation.
    if (segments.length === 0) return null;

    let acc = "";
    const crumbs = segments.map((seg, i) => {
        acc += `/${seg}`;
        return { href: acc, label: _label(seg, dynamicLabels), isLast: i === segments.length - 1 };
    });

    return (
        <nav aria-label="Breadcrumb"
             className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-8 py-2 mb-3 flex items-center gap-3"
             data-testid="breadcrumbs">
            <button type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors shrink-0"
                    data-testid="breadcrumb-back"
                    aria-label="Go back">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
            </button>
            <span className="h-4 w-px bg-border shrink-0" aria-hidden="true" />
            <ol className="flex items-center gap-1.5 text-xs tracking-wide overflow-x-auto">
                <li>
                    <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center"
                          data-testid="breadcrumb-home">
                        <HomeIcon className="h-3.5 w-3.5" />
                    </Link>
                </li>
                {crumbs.map((c) => (
                    <React.Fragment key={c.href}>
                        <ChevronRight className="h-3.5 w-3.5 opacity-40 shrink-0" />
                        <li className="shrink-0">
                            {c.isLast ? (
                                <span className="text-foreground font-semibold" data-testid={`breadcrumb-current`}>
                                    {c.label}
                                </span>
                            ) : (
                                <Link to={c.href} className="text-muted-foreground hover:text-foreground hover:underline"
                                      data-testid={`breadcrumb-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
                                    {c.label}
                                </Link>
                            )}
                        </li>
                    </React.Fragment>
                ))}
            </ol>
            {/* iter-2026 — right-aligned slot so pages can lift their primary
                page-level actions (New workspace, Add source, …) onto this row
                instead of a dedicated line. Filled via <BreadcrumbPortal>. */}
            <div id="breadcrumb-actions-slot" className="ml-auto flex items-center gap-2 shrink-0" />
        </nav>
    );
}
